import { sec } from '@/lib/security';
import { buildAuth0AuthorizationParams, getAuth0Audience } from '@/lib/auth0AuthorizationParams';

const AUTH_GETTER_WAIT_MS = 5000;
const AUTH_GETTER_POLL_MS = 100;

type AccessTokenGetter = (options?: unknown) => Promise<string>;

const trimTrailingSlash = (value: string): string => value.replace(/\/+$/, '');

export const getAdminApiAudience = (): string => getAuth0Audience();

export const getAdminApiBaseUrl = (): string => {
  const envBaseUrl = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BASE_API_URL;
  if (envBaseUrl) {
    return trimTrailingSlash(envBaseUrl);
  }
  throw new Error(
    'REACT_APP_API_BASE_URL (or REACT_APP_BASE_API_URL) is required for this build',
  );
};

/**
 * Base URL for the SEPARATE music-PROCESSING API Gateway (Slice #6 / UI-0).
 *
 * This stack has NO custom-domain mapping (intentional), so it is configured
 * independently from getAdminApiBaseUrl() via REACT_APP_MUSIC_PROCESSING_API_BASE_URL.
 * When unset outside development, returns '' and Processing pages degrade to
 * "not configured" instead of crashing.
 */
export const getMusicProcessingApiBaseUrl = (): string => {
  const envBaseUrl = process.env.REACT_APP_MUSIC_PROCESSING_API_BASE_URL;
  if (envBaseUrl) {
    return trimTrailingSlash(envBaseUrl);
  }
  // Development-only fallback (stripped from production builds by CRA DCE).
  if (process.env.REACT_APP_ENV === 'development') {
    return 'https://vqx7twx730.execute-api.us-east-1.amazonaws.com';
  }
  return '';
};

// Catalog completion: the former SL1 execute-api env-var bypass for the
// /tracks and /artists catalog APIs was retired once both received custom
// admin-domain basepath mappings (kk-admin-domain-routing). All canonical
// catalog reads now go through getAdminApiBaseUrl() like every other page.

/**
 * SL1 — stage-specific Approved-music CDN (CloudFront over the Approved
 * bucket). Playback URLs are `${cdnBase}/${approvedObjectKey}`.
 *
 * Accepts either a bare domain or a full https URL in
 * REACT_APP_MUSIC_APPROVED_CDN_DOMAIN. Development may fall back to the
 * staging distribution; production must set the env var explicitly.
 */
const normalizeCdnBaseUrl = (value: string): string => {
  const trimmed = trimTrailingSlash(value.trim());
  if (trimmed === '') return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

export const getMusicApprovedCdnBaseUrl = (): string => {
  const envDomain = process.env.REACT_APP_MUSIC_APPROVED_CDN_DOMAIN;
  if (envDomain) {
    return normalizeCdnBaseUrl(envDomain);
  }
  if (process.env.REACT_APP_ENV === 'development') {
    return normalizeCdnBaseUrl('dn3s3p3o8ywem.cloudfront.net');
  }
  return '';
};

const waitForAccessTokenGetter = async (
  maxWaitMs: number = AUTH_GETTER_WAIT_MS
): Promise<AccessTokenGetter | null> => {
  const startedAt = Date.now();
  while (Date.now() - startedAt < maxWaitMs) {
    const tokenGetter = sec.getAccessTokenSilently();
    if (tokenGetter && typeof tokenGetter === 'function') {
      return tokenGetter as AccessTokenGetter;
    }
    await new Promise((resolve) => setTimeout(resolve, AUTH_GETTER_POLL_MS));
  }
  return null;
};

export const getAdminAccessToken = async (tokenGetterOverride?: () => Promise<string>): Promise<string> => {
  if (tokenGetterOverride) {
    const overrideToken = await tokenGetterOverride();
    if (!overrideToken || overrideToken.trim() === '') {
      throw new Error('Authentication token is empty');
    }
    return overrideToken;
  }

  const tokenGetter = await waitForAccessTokenGetter();
  if (!tokenGetter) {
    throw new Error('Authentication is not ready. Please refresh and try again.');
  }

  const token = await tokenGetter({
    authorizationParams: buildAuth0AuthorizationParams(),
  });
  if (!token || token.trim() === '') {
    throw new Error('Authentication token is empty');
  }
  return token;
};
